import { onBeforeUnmount, getCurrentInstance } from 'vue';
import { on as busOn, once as busOnce, bus } from '/app/plugins/bus';

/**
 * Splits an `event@modifier` target.
 *
 * `@once` detaches the handler after the first emit. An unrecognised modifier
 * binds the literal string and warns: silently stripping it would register a
 * handler under a name nothing emits, which is invisible at runtime.
 */
const parseTarget = (target) => {
    const [event, modifier] = target.split('@');

    if (!modifier) {
        return { event: target, once: false };
    }

    if (modifier === 'once') {
        return { event, once: true };
    }

    console.warn(`[bus] Unknown modifier "@${modifier}" in "${target}"; binding the literal event name.`);

    return { event: target, once: false };
};

export function useBus(listeners) {
    const cleanups = [];

    const register = (event, isOnce, handler) => {
        const off = (isOnce ? busOnce : busOn)(event, handler);

        cleanups.push(off);

        return off;
    };

    // Accepts both `on('event', fn)` and the `on('event@once', fn)` modifier
    // form, so the string used here and in the listeners object below mean the
    // same thing wherever they appear.
    const on = (target, handler) => {
        const { event, once: isOnce } = parseTarget(target);

        return register(event, isOnce, handler);
    };

    const once = (target, handler) => register(parseTarget(target).event, true, handler);

    if (listeners) {
        const proxy = getCurrentInstance()?.proxy;

        for (const [target, handler] of Object.entries(listeners)) {
            on(target, proxy ? handler.bind(proxy) : handler);
        }
    }

    onBeforeUnmount(() => {
        cleanups.forEach(fn => fn());
    });

    return { on, once, emit: bus };
}
